import { TranslocoService } from '@jsverse/transloco';
import { PathResolveService } from '../../../services/path-resolve.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AnalysisType, ParentNavigationService } from './parent-navigation-service';
import { MatDialog } from '@angular/material/dialog';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { AnalysisApiService } from 'src/app/data-transfer/services/analysis-api-service';
import { NgxSpinnerService } from 'ngx-spinner';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { Observable } from 'rxjs';
import { EvaluationGenerationDto } from 'src/app/data-transfer/entities/evaluation-entities/evaluation-generation-dto';
import { AnalysisNotPossibleDialogComponent } from 'src/app/components/dialogs/analysis-not-possible-dialog/analysis-not-possible-dialog.component';
import { RecyclabilityResultDto } from 'src/app/data-transfer/entities/evaluation-entities/recyclability-result-dto';
import { LifeCycleResultDto } from 'src/app/data-transfer/entities/evaluation-entities/life-cycle-result-dto';
import { ExpenseResultDto } from 'src/app/data-transfer/entities/evaluation-entities/expense-result-dto';
import { ProblemInformationDto } from 'src/app/data-transfer/entities/evaluation-entities/problem-information-dto';
import moment from 'moment/min/moment-with-locales';
import { CreditsService } from 'src/app/services/credits-service';
@Injectable({ providedIn: 'root' })
export class PackagingSystemNavigationService extends ParentNavigationService {

  constructor(
    protected dialog: MatDialog,
    protected translateService: TranslocoService,
    private router: Router,
    protected analysisApiService: AnalysisApiService,
    protected creditsService: CreditsService,
    private spinner: NgxSpinnerService
  ) {
    super(dialog, translateService, analysisApiService, creditsService);
  }

  navigateToPackagingSystem(packagingSystemId: number, versionId?: number) {
    const path = versionId == null ?
      PathResolveService.getUpdatePackagingSystemPath(true, packagingSystemId) :
      PathResolveService.getPackagingSystemHistoryPath(true, packagingSystemId, versionId);
    this.router.navigate([path]);
  }

  navigateToPackagingSystemPreview(candidateId: number, isChange = false, seePackagingSystemBeforeChange = false) {
    this.router.navigate([PathResolveService.getPackagingSystemPreviewPath(true, candidateId, isChange, seePackagingSystemBeforeChange)]);
  }

  async navigateToAnalysisWithCostConfirmation(
    analysisType: AnalysisType, packagingSystemId: number, versionId: number,
    problemInformation: ProblemInformationDto[]
  ) {
    const isAnalysisPossible = problemInformation.length == 0;
    await this.creditsService.setCreditsCount();

    this.getAnalysisCost(analysisType, PackagingPart.System, packagingSystemId, versionId).subscribe(cost => {
      const availableCredits = this.getAvailableCreditsForAnalysis(analysisType);
      if (cost > availableCredits) {
        this.stopWhenCreditsInsufficient(availableCredits, cost);
        this.spinner.hide();
        return;
      }

      if (!isAnalysisPossible) {
        this.stopWhenAnalysisNotPossible(problemInformation);
      } else {
        this.navigateToAnalysis(analysisType, packagingSystemId, versionId);
      }
    });
  }

  private navigateToAnalysis(
    analysisType: AnalysisType, packagingSystemId: number, versionId: number
  ) {
    let observableAnalysis: Observable<LifeCycleResultDto[] | RecyclabilityResultDto[] | ExpenseResultDto[]> | null = null;
    const dto: EvaluationGenerationDto = { id: packagingSystemId, version: versionId };
    if (analysisType === AnalysisType.Recyclability) {
      observableAnalysis = this.analysisApiService.getRecyclabilityEvaluationResult(dto, PackagingPart.System);
    } else if (analysisType === AnalysisType.Lifecycle) {
      observableAnalysis = this.analysisApiService.getLifeCycleEvaluationResult(dto, PackagingPart.System);
    } else if (analysisType === AnalysisType.CradleToGateLifecycle) {
      observableAnalysis = this.analysisApiService.getCradleToGateEvaluationResult(dto, PackagingPart.System);
    } else if (analysisType === AnalysisType.ExpenseCalculation) {
      dto.evaluationReferenceTimestamp = moment().utc().format("yyyy-MM-DDTHH:mm:ss+00:00");
      const observableExpenseResult = this.analysisApiService.getExpenseCalculationResults(dto, PackagingPart.System);
      this.evaluationSubscription = observableExpenseResult.subscribe(analyses => {
        this.navigateToHistoryAnalysis(analysisType, packagingSystemId, versionId, [analyses[0].licenseFee?.id,
          analyses[0].ecomodulation?.id,
          analyses[0].plasticTax?.id]);
      });
    }

    if (!observableAnalysis) { return; }

    this.evaluationSubscription = observableAnalysis.subscribe(analyses => {
      this.navigateToHistoryAnalysis(analysisType, packagingSystemId, versionId, [analyses[0].id ?? '']);
      this.creditsService.setCreditsCount();
    });
  }

  private stopWhenAnalysisNotPossible(problemInformation: ProblemInformationDto[]): void {
    this.spinner.hide();
    const generalExplanation = this.translateService.translate("analysisNotPossibleInfo.generalConditionsSystem");
    const alertDialogConfig = getDialogConfig({ generalExplanation: generalExplanation, problemInformation: problemInformation}, '600px');
    this.dialog.open(AnalysisNotPossibleDialogComponent, alertDialogConfig);
  }

  navigateToHistoryAnalysis(
    analysisType: AnalysisType, packagingId: number,
    packagingVersionId: number, analysisVersionsId: (string | undefined)[]
  ) {
    let path = '';
    switch (analysisType) {
      case AnalysisType.Recyclability: {
        path = PathResolveService.getRecyclabilityAnalysisHistoryPathSystem(true, packagingId, packagingVersionId, analysisVersionsId[0]);
        break;
      }
      case AnalysisType.CradleToGateLifecycle: {
        path = PathResolveService.getCradleToGateLifecycleAnalysisHistoryPathSystem(true, packagingId, packagingVersionId, analysisVersionsId[0]);
        break;
      }
      case AnalysisType.Lifecycle: {
        path = PathResolveService.getLifecycleAnalysisHistoryPathSystem(true, packagingId, packagingVersionId, analysisVersionsId[0]);
        break;
      }
      case AnalysisType.ExpenseCalculation: {
        path = PathResolveService.getExpenseCalculationHistoryPathSystem(
          true, packagingId, packagingVersionId, analysisVersionsId[2], analysisVersionsId[0], analysisVersionsId[1])
      }
    }
    this.router.navigate([path]);
  }

  navigateToComparison(listOfIdsToCompare: number[]) {
      this.router.navigate([PathResolveService.getCompareRecyclabilityAnalysisPathSystem(true, listOfIdsToCompare)]);
  }
}
